import React, { useState, useEffect } from 'react';
import { Search, Plus, SortAsc, SortDesc, AlertTriangle } from 'lucide-react';
// Import the JSON-based configuration
import appConfig from '../config/appConfig';

const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '0',
    margin: '0'
  },
  dialog: {
    backgroundColor: 'white',
    padding: '0px 20px 10px 20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    padding: '10px 0',
    color: '#000'
  }
};

const ManageProjectAccessDialog = ({
  isOpen,
  onClose,
  selectedUserIds,
  corporateAccount,
  selectedAccountId,
  login_token,
  permissions
}) => {
  const [userProjects, setUserProjects] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAddProjectDialogOpen, setIsAddProjectDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUserProjects, setFilteredUserProjects] = useState([]);
  const [accessLevel, setAccessLevel] = useState(1);
  const [successMessage, setSuccessMessage] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Add Project Dialog specific states
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [addProjectError, setAddProjectError] = useState('');
  const [addProjectSuccess, setAddProjectSuccess] = useState('');

  // New states for Business Team and User Role
  const [businessTeams, setBusinessTeams] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [selectedBusinessTeamId, setSelectedBusinessTeamId] = useState('');
  const [selectedUserRole, setSelectedUserRole] = useState('');

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const styles = {
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '0px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      fontSize: '0.875rem',
      margin: '0',
    },
    th: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px',
      cursor: 'pointer',
      userSelect: 'none',
      position: 'relative'
    },
    thNonSortable: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px'
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #ddd',
    },
    checkbox: {
      marginRight: '10px',
    },
    deleteButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '6px 12px',
      height: '28px',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    disabled: {
      opacity: 0.6
    },
  };

  // Reset all data
  const resetAllData = () => {
    setUserProjects([]);
    setSelectedUsers(new Set());
    setError('');
    setSuccessMessage('');
    setIsLoading(false);
    setSearchTerm('');
    setFilteredUserProjects([]);
    setCurrentPage(1);
  };

  // Sorting functionality
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderDeleteConfirmDialog = () => {
    if (!isDeleteConfirmOpen || !itemToDelete) return null;

    return (
      <div style={{...DialogStyles.overlay, zIndex: 1200}}>
        <div style={{...DialogStyles.dialog, width: '600px'}}>
          <div style={{
            backgroundColor: '#FFE6E6',
            color: '#CC0000',
            padding: '15px 0',
            textAlign: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            borderRadius: '8px 8px 0 0',
            margin: '-20px -20px 20px -20px'
          }}>
            <div className="flex items-center justify-center gap-2">
              <AlertTriangle size={20} />
              <span>Confirm Project Access Removal</span>
            </div>
          </div>

          <div className="px-6 py-4">
            <p className="text-gray-700 mb-4">
              {itemToDelete.id === 'multiple'
                ? `Are you sure you want to remove project access for these records?`
                : `Are you sure you want to remove project access for this user?`}
            </p>
            <p className="text-sm text-red-600 mb-4">
              This action cannot be undone.
            </p>

            {/* Show access details */}
            <div className="bg-gray-50 rounded p-3 mb-4">
              <p className="text-xs text-gray-600 mb-2">Access details:</p>
              <div className="text-sm text-gray-700">
                {itemToDelete.id === 'multiple' ? (
                  <div><strong>Selected Records:</strong> {itemToDelete.description}</div>
                ) : (
                  <>
                    <div><strong>User ID:</strong> {itemToDelete.user_id}</div>
                    <div><strong>User Name:</strong> {itemToDelete.user_name || 'N/A'}</div>
                    <div><strong>Project ID:</strong> {itemToDelete.project_id}</div>
                    <div><strong>Project Name:</strong> {itemToDelete.project_name || 'N/A'}</div>
                    <div><strong>Access Level:</strong> {itemToDelete.access_level}</div>
                    <div><strong>Business Team:</strong> {itemToDelete.business_team_description || 'N/A'}</div>
                    <div><strong>User Role:</strong> {itemToDelete.user_role || 'N/A'}</div>
                    <div><strong>Created Date:</strong> {itemToDelete.created_date ? new Date(itemToDelete.created_date).toLocaleDateString() : 'N/A'}</div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-4 p-6 pt-0">
            <button
              onClick={() => {
                setIsDeleteConfirmOpen(false);
                setItemToDelete(null);
              }}
              style={{
                backgroundColor: '#f8f9fa',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirmed}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {itemToDelete.id === 'multiple'
                ? 'Remove Project Access'
                : 'Remove Project Access'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Apply sorting to user projects
  const sortedUserProjects = React.useMemo(() => {
    let sortableUserProjects = [...filteredUserProjects];
    if (sortConfig.key) {
      sortableUserProjects.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';

        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUserProjects;
  }, [filteredUserProjects, sortConfig]);

  // Pagination
  const totalPages = Math.ceil((sortedUserProjects?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUserProjects = (sortedUserProjects || []).slice(startIndex, startIndex + itemsPerPage);

  // Effect to fetch user projects when dialog opens
  useEffect(() => {
    if (isOpen && selectedUserIds && selectedUserIds.size > 0) {
      resetAllData();
      fetchUserProjects();
    }
  }, [isOpen, selectedUserIds, selectedAccountId]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = userProjects.filter(item =>
        (item.user_name && item.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.user_id && item.user_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.project_name && item.project_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.project_id && item.project_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.business_team_description && item.business_team_description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.user_role && item.user_role.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredUserProjects(filtered);
    } else {
      setFilteredUserProjects(userProjects);
    }
    setCurrentPage(1);
  }, [searchTerm, userProjects]);

  const fetchUserProjects = async () => {
    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    setSelectedUsers(new Set());

    if (!selectedUserIds || selectedUserIds.size === 0) {
      setError('No users selected');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(appConfig.getUrl('/get_user_project_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
            requesting_for_corporate_account: selectedAccountId,
          user_ids: Array.from(selectedUserIds)
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setUserProjects(data.user_project_list || []);
        setFilteredUserProjects(data.user_project_list || []);
      } else if (data.status === 'Failed' && data.status_description === 'No user projects found matching the criteria') {
        setUserProjects([]);
        setFilteredUserProjects([]);
      } else {
        throw new Error(data.status_description || 'Failed to fetch user projects');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching user projects:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await fetch(appConfig.getUrl('/get_project_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: selectedAccountId
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setProjects(data.project_list || []);
      } else {
        throw new Error(data.status_description || 'Failed to fetch projects');
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setAddProjectError('Failed to load available projects');
    }
  };

  // New function to fetch business teams
  const fetchBusinessTeams = async (projectId) => {
    try {
      const response = await fetch(appConfig.getUrl('/get_business_team_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: selectedAccountId,
          project_id: projectId
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setBusinessTeams(data.business_team_list || []);
      } else {
        setBusinessTeams([]);
        console.error('Failed to fetch business teams:', data.status_description);
      }
    } catch (err) {
      console.error('Error fetching business teams:', err);
      setBusinessTeams([]);
    }
  };

  // New function to fetch user roles
  const fetchUserRoles = async (projectId) => {
    console.log('Fetching user roles for project:', projectId);
    console.log('Selected Account ID:', selectedAccountId);

    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: selectedAccountId,
          project_id: projectId,
          entity: "USER ROLES"
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setUserRoles(data.entity_statuses || []);
      } else {
        setUserRoles([]);
        console.error('Failed to fetch user roles:', data.status_description);
      }
    } catch (err) {
      console.error('Error fetching user roles:', err);
      setUserRoles([]);
    }
  };

  const handleAddProject = async () => {
    if (!permissions.canAssignProjects) {
      setAddProjectError('Insufficient permission to add project access');
      return;
    }

    setIsLoading(true);
    setAddProjectError('');
    setAddProjectSuccess('');

    // Validate project selection
    if (!selectedProjectId) {
      setAddProjectError('Please select a project before adding access');
      setIsLoading(false);
      return;
    }

    if (!selectedUserIds || selectedUserIds.size === 0) {
      setAddProjectError('No users selected');
      setIsLoading(false);
      return;
    }

    try {
      // Prepare the request body with optional fields
      const requestBody = {
        corporate_account: corporateAccount,
        requesting_for_corporate_account: selectedAccountId,
        project_id: selectedProjectId,
        user_ids: Array.from(selectedUserIds),
        access_level: accessLevel
      };

      // Add optional fields only if they are selected
      if (selectedBusinessTeamId) {
        requestBody.business_team_id = selectedBusinessTeamId;
      }

      if (selectedUserRole) {
        requestBody.user_role = selectedUserRole;
      }
    console.log('Request body for updating access level 2:', requestBody);

      const response = await fetch(appConfig.getUrl('/update_user_project_access'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setAddProjectSuccess(data.status_description || 'Project access added successfully');

        setTimeout(async () => {
          await fetchUserProjects();
          setSuccessMessage('Project access added successfully');
          setIsAddProjectDialogOpen(false);
          setSelectedProjectId('');
          setProjectSearchTerm('');
          setSelectedBusinessTeamId('');
          setSelectedUserRole('');
          setAddProjectSuccess('');
          setAddProjectError('');

          setTimeout(() => {
            setSuccessMessage('');
          }, 3000);
        }, 2000);
      } else {
        throw new Error(data.status_description || 'Failed to add project access');
      }
    } catch (err) {
      setAddProjectError(err.message);
      console.error('Error adding project access:', err);
    } finally {
      setIsLoading(false);
    }
  };




 const handleUpdateAccessLevel = async (userProject, newLevel) => {
  if (!permissions.canAssignProjects) {
    setError('Insufficient permission to update access level');
    return;
  }

  setIsLoading(true);
  setError('');
  setSuccessMessage('');

  try {
    // Prepare the request body with existing values
    const requestBody = {
      corporate_account: corporateAccount,
      requesting_for_corporate_account: selectedAccountId,
      user_ids: Array.from(selectedUserIds),
      project_id: userProject.project_id,
      access_level: newLevel
    };

    // Include existing business_team_id if it exists
    if (userProject.business_team_id) {
      requestBody.business_team_id = userProject.business_team_id;
    }

    // Include existing user_role if it exists
    if (userProject.user_role) {
      requestBody.user_role = userProject.user_role;
    }
    console.log('Request body for updating access level 1:', requestBody);
    console.log('userProject :', userProject);

    const response = await fetch(appConfig.getUrl('/update_user_project_access'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();
    if (data.status === 'Success') {
      setSuccessMessage(data.status_description || `Access level updated for ${userProject.user_name || userProject.user_id}`);

      setTimeout(async () => {
        await fetchUserProjects();
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }, 1000);
    } else {
      throw new Error(data.status_description || 'Failed to update access level');
    }
  } catch (err) {
    setError(err.message);
    console.error('Error updating access level:', err);
  } finally {
    setIsLoading(false);
  }
};






  const handleDeleteSelected = async () => {
    if (!permissions.canAssignProjects) {
      setError('Insufficient permission to remove project access');
      return;
    }

    if (selectedUsers.size === 0) {
      setError('No project access records selected for removal');
      return;
    }

    // For single selection, show detailed confirmation
    if (selectedUsers.size === 1) {
      const selectedKey = Array.from(selectedUsers)[0];
      const [userId, projectId] = selectedKey.split('|||');
      const userProject = userProjects.find(item =>
        item.user_id === userId && item.project_id === projectId
      );

      setItemToDelete({
        id: selectedKey,
        user_id: userProject?.user_id,
        user_name: userProject?.user_name,
        project_id: userProject?.project_id,
        project_name: userProject?.project_name,
        access_level: userProject?.access_level,
        business_team_description: userProject?.business_team_description,
        user_role: userProject?.user_role,
        created_date: userProject?.created_date
      });

      setIsDeleteConfirmOpen(true);
      return;
    }

    // For multiple selections, create a summary object
    const multipleItemsObject = {
      id: 'multiple',
      description: `${selectedUsers.size} selected project access record${selectedUsers.size > 1 ? 's' : ''}`,
      actualIds: Array.from(selectedUsers)
    };

    setItemToDelete(multipleItemsObject);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    setIsDeleteConfirmOpen(false);
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Handle both single and multiple deletions
      const recordsKeys = itemToDelete.id === 'multiple'
        ? itemToDelete.actualIds
        : [itemToDelete.id];

      const recordsToDelete = recordsKeys.map(key => {
        const [userId, projectId] = key.split('|||');
        return { user_id: userId, project_id: projectId };
      });

      const response = await fetch(appConfig.getUrl('/delete_user_project_access'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
            requesting_for_corporate_account: selectedAccountId,
          user_project_records: recordsToDelete
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        const message = recordsKeys.length === 1
          ? 'Successfully removed project access record'
          : `Successfully removed ${recordsKeys.length} project access record(s)`;

        setSuccessMessage(message);
        setSelectedUsers(new Set());

        setTimeout(async () => {
          setFilteredUserProjects([]);
          setUserProjects([]);
          await fetchUserProjects();

          setTimeout(() => {
            setSuccessMessage('');
          }, 5000);
        }, 3000);
      } else {
        throw new Error(data.status_description || 'Failed to remove project access');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error removing project access:', err);
    } finally {
      setIsLoading(false);
      setItemToDelete(null);
    }
  };

  const handleAddProjectClick = () => {
    if (!permissions.canAssignProjects) {
      setError('Insufficient permission to add project access');
      return;
    }

    setSelectedProjectId('');
    setProjectSearchTerm('');
    setSelectedBusinessTeamId('');
    setSelectedUserRole('');
    setShowProjectDropdown(false);
    setAddProjectError('');
    setAddProjectSuccess('');
    setIsAddProjectDialogOpen(true);

    // Load projects when dialog opens
    fetchProjects();
    // Load user roles with ALL PROJECTS initially since no project is selected yet
    fetchUserRoles();
  };

  const handleProjectSelect = (project) => {
    setSelectedProjectId(project.project_id);
    setProjectSearchTerm(`${project.project_name || project.project_id} (${project.project_id})`);
    setShowProjectDropdown(false);

    // Reset business team and user role selections when project changes
    setSelectedBusinessTeamId('');
    setSelectedUserRole('');

    // Fetch business teams and user roles for the selected project
    fetchBusinessTeams(project.project_id);
    fetchUserRoles(project.project_id);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === paginatedUserProjects.length) {
      setSelectedUsers(new Set());
    } else {
      const newSelected = new Set(paginatedUserProjects.map(item => `${item.user_id}|||${item.project_id}`));
      setSelectedUsers(newSelected);
    }
  };

  const toggleRowSelection = (userId, projectId) => {
    const key = `${userId}|||${projectId}`;
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedUsers(newSelected);
  };

  const accessLevelDescriptions = {}; // Remove descriptions

  // Filter projects based on search term
  const filteredProjects = projects.filter(project =>
    projectSearchTerm === '' ||
    (project.project_name && project.project_name.toLowerCase().includes(projectSearchTerm.toLowerCase())) ||
    (project.project_id && project.project_id.toLowerCase().includes(projectSearchTerm.toLowerCase()))
  );

  // Add Project Dialog Content
  const renderAddProjectDialog = () => {
    if (!isAddProjectDialogOpen) return null;

    return (
      <div style={DialogStyles.overlay}>
        <div style={{...DialogStyles.dialog, width: '700px'}}>
          <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF', height: 40 }}>Add Project Access</h2>

          <div className="mb-6 p-4 border border-gray-300 rounded-md">
            <h3 className="text-base font-semibold mb-3">
              Adding project access for: <strong>{selectedUserIds.size} selected user(s)</strong>
            </h3>
            <div className="text-sm text-gray-600">
              Selected users: {Array.from(selectedUserIds).join(', ')}
            </div>
          </div>

          {/* Project Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Select Project: <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Start typing to search projects..."
                value={projectSearchTerm}
                onChange={(e) => {
                  setProjectSearchTerm(e.target.value);
                  setShowProjectDropdown(true);
                  // Clear selection if user is typing
                  if (e.target.value !== `${projects.find(p => p.project_id === selectedProjectId)?.project_name || selectedProjectId} (${selectedProjectId})`) {
                    setSelectedProjectId('');
                    setSelectedBusinessTeamId('');
                  }
                }}
                onFocus={() => {
                  setShowProjectDropdown(true);
                  // Load projects if not already loaded
                  if (projects.length === 0) {
                    fetchProjects();
                  }
                }}
                onBlur={() => setTimeout(() => setShowProjectDropdown(false), 200)}
                className={`w-full p-2 border rounded ${!selectedProjectId && addProjectError ? 'border-red-500' : ''}`}
                disabled={isLoading}
                required
              />

              {showProjectDropdown && (
                <div className="absolute z-10 w-full max-h-60 overflow-y-auto border rounded mt-1 bg-white shadow-lg">
                  {projects.length === 0 ? (
                    <div className="p-2 text-gray-500">Loading projects...</div>
                  ) : filteredProjects.length === 0 ? (
                    <div className="p-2 text-gray-500">No projects found</div>
                  ) : (
                    filteredProjects.map(project => (
                      <div
                        key={project.project_id}
                        className="p-2 cursor-pointer hover:bg-blue-50"
                        onClick={() => handleProjectSelect(project)}
                      >
                        {project.project_name || project.project_id} ({project.project_id})
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Business Team Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Project Team: <span className="text-gray-400">(Optional)</span>
            </label>
            <select
              value={selectedBusinessTeamId}
              onChange={(e) => setSelectedBusinessTeamId(e.target.value)}
              className="w-full p-2 border rounded"
              disabled={isLoading || !selectedProjectId}
            >
              <option value="">Select a project team (optional)...</option>
              {businessTeams
                .filter(team => team.business_team_status === 'Active')
                .map(team => (
                  <option key={team.business_team_id} value={team.business_team_id}>
                    {team.business_team_description}
                  </option>
                ))
              }
            </select>
            {!selectedProjectId && (
              <div className="text-xs text-gray-500 mt-1">
                Please select a project first to load business teams
              </div>
            )}
          </div>

          {/* User Role Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              User Role: <span className="text-gray-400">(Optional)</span>
            </label>
            <select
              value={selectedUserRole}
              onChange={(e) => setSelectedUserRole(e.target.value)}
              className="w-full p-2 border rounded"
              disabled={isLoading}
            >
              <option value="">Select a user role (optional)...</option>
              {userRoles.map(role => (
                <option key={role.status} value={role.status}>
                  {role.status}
                </option>
              ))}
            </select>
          </div>

          {/* Access Level Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Access Level: <span className="text-red-500">*</span>
            </label>
            <select
              value={accessLevel}
              onChange={(e) => setAccessLevel(Number(e.target.value))}
              className="w-full p-2 border rounded"
              disabled={isLoading}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(level => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          {/* Error and Success messages */}
          {addProjectError && (
            <div className="mb-4 p-2 rounded bg-red-50 text-red-500">
              {addProjectError}
            </div>
          )}

          {addProjectSuccess && (
            <div className="mb-4 p-2 rounded bg-green-50 text-green-700">
              {addProjectSuccess}
            </div>
          )}

          <div className="flex justify-center gap-4 mt-6">
            <button
              onClick={() => setIsAddProjectDialogOpen(false)}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              disabled={isLoading}
            >
              Close
            </button>
            <button
              onClick={handleAddProject}
              style={{
                padding: '8px 16px',
                backgroundColor: (isLoading || !selectedProjectId) ? '#cccccc' : '#C8F4DE',
                color: (isLoading || !selectedProjectId) ? '#666666' : 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: (isLoading || !selectedProjectId) ? 'not-allowed' : 'pointer'
              }}
              disabled={isLoading || !selectedProjectId}
            >
              {isLoading ? 'Adding...' : 'Add Project Access'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div style={DialogStyles.overlay}>
        <div style={{
          ...DialogStyles.dialog,
          width: '1600px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          gap: '0',
          margin: '0'
        }}>

          {/* Header */}
          <div className="px-6 pt-0 pb-0">
            <div className="flex items-center mb-4 mt-0 justify-between bg-[#CCCCFF] p-3 rounded">
              {/* Search bar */}
              <div className="flex items-center">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      fetchUserProjects();
                    }
                  }}
                  placeholder="Search user projects..."
                  className="p-2 pr-10 border rounded w-full"
                />
                <button
                  onClick={() => fetchUserProjects()}
                  className="ml-2 p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Search size={18} />
                </button>
              </div>

              <h1 style={{
                flex: 1,
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'black',
                margin: 0
              }}>
                Manage Project Access - {selectedUserIds.size} User(s) Selected
              </h1>

              {/* Add Project Access button */}
              <div className="group relative">
                <button
                  className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${!permissions.canAssignProjects ? 'opacity-50' : ''}`}
                  onClick={handleAddProjectClick}
                  disabled={isLoading || !permissions.canAssignProjects}
                >
                  <Plus size={18} className="mr-2" />
                  Add Project Access
                </button>

                {!permissions.canAssignProjects && (
                  <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                    Insufficient permission
                    <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          <div className="px-6">
            {error && (
              <div className="mb-4 p-2 rounded bg-red-50 text-red-500">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="mb-4 p-2 rounded bg-green-50 text-green-700">
                {successMessage}
              </div>
            )}

            {userProjects.length === 0 && !isLoading && !error && (
              <div className="mb-4 p-2 rounded bg-yellow-50 text-yellow-700">
                No project access found for selected users
              </div>
            )}
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto px-6">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.thNonSortable}>
                    <input
                      type="checkbox"
                      checked={paginatedUserProjects.length > 0 && selectedUsers.size === paginatedUserProjects.length}
                      onChange={toggleSelectAll}
                      disabled={paginatedUserProjects.length === 0}
                      style={styles.checkbox}
                    />
                    Select All
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('user_id')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>User ID</span>
                      <span className="ml-1">
                        {sortConfig.key === 'user_id' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('user_name')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>User Name</span>
                      <span className="ml-1">
                        {sortConfig.key === 'user_name' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('project_id')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>Project ID</span>
                      <span className="ml-1">
                        {sortConfig.key === 'project_id' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('project_name')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>Project Name</span>
                      <span className="ml-1">
                        {sortConfig.key === 'project_name' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('business_team_description')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>Business Team</span>
                      <span className="ml-1">
                        {sortConfig.key === 'business_team_description' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('user_role')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>User Role</span>
                      <span className="ml-1">
                        {sortConfig.key === 'user_role' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('access_level')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>Access Level</span>
                      <span className="ml-1">
                        {sortConfig.key === 'access_level' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('created_date')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>Created Date</span>
                      <span className="ml-1">
                        {sortConfig.key === 'created_date' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('updated_date')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>Updated Date</span>
                      <span className="ml-1">
                        {sortConfig.key === 'updated_date' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-8">Loading...</td>
                  </tr>
                ) : paginatedUserProjects.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-8 text-gray-500">
                      No project access found. Add project access or adjust your search.
                    </td>
                  </tr>
                ) : (
                  paginatedUserProjects.map((item, index) => (
                    <tr
                      key={`${item.user_id}-${item.project_id}-${index}`}
                      className="border-t hover:bg-gray-50"
                      style={{
                        backgroundColor: selectedUsers.has(`${item.user_id}|||${item.project_id}`) ? '#DEEFF5' : 'transparent'
                      }}
                    >
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(`${item.user_id}|||${item.project_id}`)}
                          onChange={() => toggleRowSelection(item.user_id, item.project_id)}
                          style={styles.checkbox}
                        />
                      </td>
                      <td style={styles.td}>{item.user_id || 'N/A'}</td>
                      <td style={styles.td}>{item.user_name || 'N/A'}</td>
                      <td style={styles.td}>{item.project_id || 'N/A'}</td>
                      <td style={styles.td}>{item.project_name || 'N/A'}</td>
                      <td style={styles.td}>{item.business_team_description || 'N/A'}</td>
                      <td style={styles.td}>{item.user_role || 'N/A'}</td>
                      <td style={styles.td}>
                        {/* Access Level dropdown with permission check and tooltip */}
                        <div className="group relative">
                          <select
                            value={item.access_level}
                            onChange={(e) => {
                              if (permissions.canAssignProjects) {
                                handleUpdateAccessLevel(item, Number(e.target.value));
                              }
                            }}
                            className={`p-1 border rounded w-16 text-center ${!permissions.canAssignProjects ? 'opacity-50 ' : ''}`}
                            disabled={!permissions.canAssignProjects}
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(level => (
                              <option key={level} value={level}>
                                {level}
                              </option>
                            ))}
                          </select>

                          {/* Tooltip for insufficient permission */}
                          {!permissions.canAssignProjects && (
                            <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                              Insufficient permission
                              <div className="absolute right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-200"></div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={styles.td}>{item.created_date ? new Date(item.created_date).toLocaleDateString() : 'N/A'}</td>
                      <td style={styles.td}>{item.updated_date ? new Date(item.updated_date).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          {!isLoading && sortedUserProjects.length > 0 && (
            <div className="mx-6 mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Total Records: {sortedUserProjects.length}</span>
                <span>| Showing {sortedUserProjects.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, sortedUserProjects.length)} of {sortedUserProjects.length}</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  aria-label="Next page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="flex items-center">
                <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded p-1 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}

          {/* Footer with action buttons */}
          <div className="p-6 pt-4 border-t">
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                {/* Remove Selected button with permission check and tooltip */}
                <div className="group relative">
                  <button
                    style={{
                      ...styles.deleteButton,
                      padding: '8px 16px',
                      ...(isLoading || selectedUsers.size === 0 || !permissions.canAssignProjects ? styles.disabled : {})
                    }}
                    onClick={handleDeleteSelected}
                    disabled={isLoading || selectedUsers.size === 0 || !permissions.canAssignProjects}
                  >
                    {isLoading ? 'Removing...' : `Remove Selected (${selectedUsers.size})`}
                  </button>

                  {/* Tooltip for insufficient permission */}
                  {!permissions.canAssignProjects && selectedUsers.size > 0 && (
                    <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                      Insufficient permission
                      <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {selectedUsers.size > 0 ? `${selectedUsers.size} selected` : ''}
                </span>
                <button
                  style={{
                    backgroundColor: '#FFFDD0',
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Add Project Dialog */}
        {renderAddProjectDialog()}

        {/* Delete Confirmation Dialog */}
        {renderDeleteConfirmDialog()}

      </div>
    </div>
  );
};

export default ManageProjectAccessDialog;